import type { AuthQueryScope } from "~/lib/query-keys";
import { queryKeys, withAuthQueryScope } from "~/lib/query-keys";
import type { AppRouterContext } from "~/lib/router-runtime";
import type { PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";

const USER_FEATURE_FLAGS_STALE_TIME_MS = 30_000;
const GLOBAL_FEATURE_FLAGS_STALE_TIME_MS = 30_000;

export const PORTFOLIO_SELL_MAX_FEATURE_KEY = "portfolio_sell_max" as const;

export const AGENTS_RAIL_PORTFOLIO_CREATION_FLOW_FEATURE_KEY =
  "agents_rail_portfolio_creation_flow" as const;

export const ONDO_DIRECT_PORTFOLIO_REBALANCE_FEATURE_KEY =
  "ondo_direct_portfolio_rebalance" as const;

export const MULTI_ISSUER_TOKENIZED_ISSUANCES_FEATURE_KEY =
  "multi_issuer_tokenized_issuances_ui" as const;

export const POSITIONS_IN_SHARES_UI_FEATURE_KEY =
  "positions_in_shares_ui" as const;

export const EXECUTION_FUND_PRESTEPS_FEATURE_KEY =
  "execution_fund_presteps" as const;

export const WEBAPP_V2_CASH_ROLLUP_FEATURE_KEY =
  "webapp_v2_cash_rollup" as const;

export const EARN_ACCUMULATOR_FEATURE_KEY = "earn_accumulator" as const;

/** Attach the owner's Swig main wallet as the Investing Account's Solana vault. */
export const INVESTING_ACCOUNT_SOLANA_VAULT_FEATURE_KEY =
  "investing_account_solana_vault" as const;

export const CODEX_WALLET_BALANCE_DISCOVERY_FEATURE_KEY =
  "webapp_v2_codex_wallet_balance_discovery" as const;

/** EXPERIMENTAL Solana Multiply proof of concept (GLI-1918): the URL-only leveraged-position page. */
export const SOLANA_MULTIPLY_POC_FEATURE_KEY = "solana_multiply_poc" as const;

export function getGlobalFeatureFlagsQueryOptions(input: {
  featureKeys: readonly string[];
  platformApi: PlatformApiTrpcClient;
}) {
  const featureKeys = Array.from(new Set(input.featureKeys)).sort();

  return {
    queryKey: queryKeys.featureFlags.globalFlagsByKeys(featureKeys),
    queryFn: async () =>
      await input.platformApi.featureFlags.getGlobalFlags.query({
        featureKeys,
      }),
    retry: false,
    staleTime: GLOBAL_FEATURE_FLAGS_STALE_TIME_MS,
  } as const;
}

export function getUserFeatureFlagsQueryOptions(input: {
  authQueryScope: AuthQueryScope;
  platformApi: PlatformApiTrpcClient;
}) {
  return {
    queryKey: withAuthQueryScope(
      queryKeys.featureFlags.userFlags,
      input.authQueryScope,
    ),
    queryFn: async () =>
      await input.platformApi.featureFlags.getUserFlags.query(),
    retry: false,
    staleTime: USER_FEATURE_FLAGS_STALE_TIME_MS,
  } as const;
}

export function getUserFeatureFlagsRouteQueryOptions(
  context: AppRouterContext,
) {
  return getUserFeatureFlagsQueryOptions({
    authQueryScope: context.auth.authQueryScope,
    platformApi: context.platformApi,
  });
}
